import React from 'react'
import {useTranslation} from 'react-i18next'
import {ConversionCta} from '../../domain/entities/ConversionPipeline.ts'
import {ctaLabel} from './pipelineView.ts'
import {Spinner} from './PipelineParts.tsx'
import {RunConversionCta} from './useConversionCtaRunner.tsx'

const MessageIcon: React.FC = () => (
    <svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-none">
        <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z"/>
    </svg>
)

const COMPACT = {
    primary: 'h-7 px-2.5 rounded-lg bg-neutralBlack-900 text-white text-[12px] font-semibold hover:bg-neutralBlack-700',
    ghost: 'h-7 px-2.5 rounded-lg border border-neutralBlack-200 bg-white text-[12px] font-medium text-neutralBlack-900 hover:bg-neutralBlack-100',
}
const PROMINENT = {
    primary: 'h-9 px-4 rounded-[10px] bg-neutralBlack-900 text-white text-[13px] font-semibold hover:bg-neutralBlack-700',
    ghost: 'h-9 px-3.5 rounded-[10px] border border-neutralBlack-200 bg-white text-[13px] font-medium text-neutralBlack-900 hover:bg-neutralBlack-100',
}

// The CTAs as buttons: the first one is the main next step. `compact` on a
// card, larger in the detail panel.
export const ConversionCtaButtons: React.FC<{
    conversionId: string
    ctas: ConversionCta[] | undefined
    variant: 'compact' | 'prominent'
    isRunning: (id: string, cta: ConversionCta) => boolean
    disabled?: boolean
    onRun: RunConversionCta
}> = ({conversionId, ctas, variant, isRunning, disabled, onRun}) => {
    const {t} = useTranslation('dashboard')
    if (!ctas?.length) return null
    const styles = variant === 'compact' ? COMPACT : PROMINENT
    return (
        <div className={`flex items-center flex-wrap ${variant === 'compact' ? 'gap-1.5' : 'gap-2'}`}>
            {ctas.map((cta, index) => {
                const label = ctaLabel(cta, t)
                const busy = isRunning(conversionId, cta)
                return (
                    <button key={cta.key} type="button" disabled={disabled || busy}
                            title={cta.sendsMessage ? t('pipelines.cta.sendsMessage') : undefined}
                            onClick={(e) => { e.stopPropagation(); onRun(conversionId, cta) }}
                            className={`${index === 0 ? styles.primary : styles.ghost} inline-flex items-center gap-1.5 transition-colors duration-200 ease-out disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutralBlack-900`}>
                        {busy ? <Spinner/> : cta.sendsMessage && <MessageIcon/>}
                        <span>{label}</span>
                        {cta.sendsMessage && <span className="sr-only">, {t('pipelines.cta.sendsMessage')}</span>}
                    </button>
                )
            })}
        </div>
    )
}
